/**
 * Whether a code opens a room.
 *
 * A room is a realtime channel that exists while somebody is standing in it,
 * so "does this room exist" cannot be asked of the channel — only of the note
 * beside it. The note carries when a browser last reported being there; this
 * module turns that into the one decision the entry screen needs, with no
 * clock, no network and no React of its own.
 */

/** The room a code was recorded under, as the decision needs to see it. */
export interface RoomRecord {
  code: string;
  /** Permanent rooms never go cold: the demo room is the first of them. */
  isPermanent: boolean;
  /** When a browser last reported standing in the room, in epoch ms. */
  lastSeenAt: number;
  /** A room belonging to an account keeps its code when it empties. */
  accountOwned: boolean;
}

/**
 * How long after the last sign of life a room still opens. Long enough that a
 * reconnect, a network handover or a locked phone never shuts the door on the
 * people already inside.
 */
export const ROOM_GRACE_WINDOW_MS = 120_000;

/** One beat, then a reconnect, still lands inside the grace window. */
export const ROOM_HEARTBEAT_INTERVAL_MS = 45_000;

/** The permanent demo room, so there is always a campfire to look at. */
export const DEMO_ROOM_CODE = "warm-campfire-1";

/**
 * One paid campaign, one permanent room: every ad lands on a room of its own so
 * a campaign's joins are never another's. Entering any of them reports the same
 * demo-room conversion, which is what each of those campaigns bids toward.
 */
export const CAMPAIGN_ROOM_CODES = [
  DEMO_ROOM_CODE,
  "campfire-circle-1",
  "open-campfire-1",
] as const;

/** Whether entering this room is what a paid campaign pays for. */
export function isCampaignRoom(roomId: string | undefined): boolean {
  return CAMPAIGN_ROOM_CODES.some((code) => code === roomId);
}

/**
 * The account every room nobody signed in created is recorded against — the
 * demo room first of all. No visitor can ever sign in as it, so a system-owned
 * room has no host by ownership and elects one the ordinary way; and it is
 * changed only through the backend, never from inside the room.
 */
export const SYSTEM_ROOM_OWNER_ID = "00000000-0000-0000-0000-00000000cafe";

/**
 * `unknown` is not a third kind of room: it is the lookup itself failing, and
 * it deliberately lets the visitor in. A backend hiccup must never lock
 * everybody out of a room that is plainly running.
 */
export type RoomAccess = "open" | "closed" | "unknown";

/**
 * Whether a room belongs to somebody who can come back to it.
 *
 * Creating a room has required an account since `accounts`' "Creating a room
 * requires an account", so in practice this is true of every room a visitor
 * made. What it is false of is the rooms recorded against
 * `SYSTEM_ROOM_OWNER_ID` — the demo and campaign rooms, which are permanent by
 * their own flag — and rows from before accounts were asked for, which nobody
 * can claim and which go cold as they always did.
 */
/**
 * A room opens while it is permanent, while an account holds it, or while it is
 * still inside the grace window after the last sign of life.
 *
 * The middle one is what lets "our Tuesday campfire" be the same room every
 * week. Before it, a code died two minutes after the last person left, so a
 * link sent on Monday was refused by Wednesday and an account could not have a
 * room of its own at all.
 */
export function isRoomOpen(record: RoomRecord | null, now: number): boolean {
  if (!record) return false;
  if (record.isPermanent) return true;
  if (record.accountOwned) return true;
  return now - record.lastSeenAt <= ROOM_GRACE_WINDOW_MS;
}

/** The decision as the entry screen consumes it. */
export function roomAccessFor(
  record: RoomRecord | null,
  now: number,
): RoomAccess {
  return isRoomOpen(record, now) ? "open" : "closed";
}

/**
 * Why a visitor is standing outside a room, as a reason rather than as a
 * sentence.
 *
 * This layer is framework-free and cannot translate anything, so it used to
 * hold the English a visitor read — which meant a Russian visitor read English
 * and no gate could see it. It names the reason now and the seam says it.
 *
 * `needs-account`: creating a room costs TURN and realtime capacity, so it is
 * asked of a named visitor. Joining stays free, which is why this is a
 * create-only refusal. `room-closed`: the code opens nothing.
 * `invalid-code`: the string is not a room code (`isRoomCode`), so it is
 * refused before anything is asked or joined.
 * `removed-by-host`: they were in the room and the host removed them. It is the
 * one reason here that is not about a code, which is why the hint about
 * mistyping one is not shown beside it. Their browser remembers the room
 * (`roomRemovals`), so the same code typed again is refused for the same
 * reason; another browser is let back in.
 */
export type EntryRefusal =
  "needs-account" | "room-closed" | "invalid-code" | "removed-by-host";
